import { apiFetch } from "../api"
import { cabecalhosDoApp } from "@/lib/cabecalhos-do-app"
import authService from "./auth-service"
import { dedupeRequest } from "../utils/dedupe-request"

/**
 * Um menu do aluno diferente para uma categoria ou um gênero. `secoes` guarda só
 * o que muda do menu de todos (`secoes_visiveis`): o resto segue ele.
 */
export type GrupoDoMenu =
  | { tipo: "CATEGORIA"; categoria_id: string; secoes: Record<string, boolean> }
  | { tipo: "GENERO"; genero: "MASCULINO" | "FEMININO"; secoes: Record<string, boolean> }

export interface PersonalTema {
  id: string
  personal_id: string
  cor_primaria: string
  cor_primaria_dim?: string
  cor_background?: string
  cor_surface_1?: string
  cor_surface_2?: string
  cor_surface_3?: string
  cor_surface_4?: string
  modo_padrao?: "dark" | "light"
  logo_url?: string | null
  logo_url_light?: string | null
  logo_titulo?: string | null
  logo_escala?: number
  msg_boas_vindas?: string | null
  secoes_visiveis?: string | Record<string, boolean>
  /**
   * Os menus diferentes por categoria ou gênero. Só chega na leitura do
   * PERSONAL: para o aluno o servidor já manda o menu dele resolvido.
   */
  menu_por_grupo?: GrupoDoMenu[] | null
  /**
   * A personalização vale AGORA, segundo o plano do personal.
   *
   * `false` é ordem de limpeza: apaga cor, logo, nome e escala do aparelho (ver
   * `brand-theme-context`). Quem responde é o servidor, porque o app deduzia
   * pela cor recebida e dedução não separa "nunca personalizou" de
   * "personalizou e perdeu o direito".
   *
   * `undefined` = API antiga, sem o campo: aí vale a dedução de antes.
   */
  personalizacao_ativa?: boolean
}

class TemaService {
  /**
   * Busca o tema do personal logado.
   * Retorna null se não tiver tema customizado.
   */
  async buscarMeuTema(): Promise<PersonalTema | null> {
    return dedupeRequest("tema:meu", async () => {
      try {
        return await apiFetch<PersonalTema>("/pessoal/tema")
      } catch {
        return null
      }
    })
  }

  /**
   * Busca o tema do personal do aluno logado.
   * Retorna null se o personal não tiver tema customizado.
   */
  async buscarTemaDoMeuPersonal(): Promise<PersonalTema | null> {
    return dedupeRequest("tema:meu-personal", async () => {
      try {
        return await apiFetch<PersonalTema>("/meu-tema")
      } catch {
        return null
      }
    })
  }

  /**
   * Atualiza o tema do personal logado.
   */
  async atualizarTema(tema: Partial<Omit<PersonalTema, "id" | "personal_id">>): Promise<PersonalTema> {
    return await apiFetch<PersonalTema>("/pessoal/tema", {
      method: "PATCH",
      body: JSON.stringify(tema),
    })
  }

  async uploadLogo(arquivo: File, variante: "dark" | "light" = "dark"): Promise<{ url: string }> {
    const token = await authService.getValidToken()
    const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.welltrainer.com.br"
    const formData = new FormData()
    formData.append("logo", arquivo)
    const endpoint = variante === "light" ? "/pessoal/tema/logo-light" : "/pessoal/tema/logo"
    const res = await fetch(`${baseUrl}${endpoint}`, {
      method: "POST",
      headers: token ? { ...cabecalhosDoApp(), Authorization: token } : {},
      body: formData,
    })
    if (!res.ok) throw new Error("Erro ao enviar logo.")
    return res.json()
  }
}

const temaService = new TemaService()
export default temaService
